import React, { useState, useEffect } from 'react';
import { X, Wine, User, Phone, Mail, MapPin, Calendar, Truck, FileText, CheckCircle, AlertTriangle, ArrowRight, RefreshCw, Download, CreditCard, DollarSign, Receipt, Loader } from 'lucide-react';
import {
  promoSummerOrderService,
  PROMO_STATUS_LABELS,
  PROMO_STATUS_COLORS,
  PROMO_STATUS_ORDER,
} from '../../../services/promoSummerOrderService';
import { invoiceService } from '../../../services/invoiceService';
import { partnerAccountingService } from '../../../services/partnerAccountingService';
import { supabase } from '../../../lib/supabase';

// ─── Main component ───────────────────────────────────────────────────────────
export default function PromoOrderDetail({ order: initialOrder, onClose, onUpdated }) {
  const [order, setOrder] = useState(initialOrder);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [converting, setConverting] = useState(false);
  const [collectionDate, setCollectionDate] = useState(order?.collection_planned_date || '');
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceAction, setInvoiceAction] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const qty = order?.barrel_quantity || 1;
  const unitPromoPrice = Number(order?.unit_promo_price) || 160;
  const unitDepositAmount = Number(order?.unit_deposit_amount) || 50;
  const unitBalanceAmount = Number(order?.unit_balance_amount) || 110;
  const totalPromo = qty * unitPromoPrice;
  const totalDeposit = qty * unitDepositAmount;
  const totalBalance = qty * unitBalanceAmount;
  const totalProvider = qty * (Number(order?.unit_provider_cost) || 30);
  const totalADT = qty * (Number(order?.unit_adt_revenue) || 20);

  // Load invoices on mount
  useEffect(() => {
    loadInvoices();
  }, [order?.id]);

  const loadInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const data = await invoiceService?.getPromoOrderInvoices(order?.id);
      setInvoices(data || []);
    } catch (err) {
      console.error('Erreur chargement factures:', err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const depositInvoice = invoices?.find(inv => inv?.invoice_type === 'deposit');
  const finalInvoice = invoices?.find(inv => inv?.invoice_type === 'final' || inv?.invoice_type === 'balance');

  const handleStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      setError('');
      const updated = await promoSummerOrderService?.updateStatus(order?.id, newStatus);
      setOrder(updated);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setSaving(false);
    }
  };

  const handleRelance = async () => {
    try {
      setSaving(true);
      const updated = await promoSummerOrderService?.markRelance(order?.id);
      setOrder(updated);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la relance');
    } finally {
      setSaving(false);
    }
  };

  const handlePlanCollection = async () => {
    if (!collectionDate) return;
    try {
      setSaving(true);
      const updated = await promoSummerOrderService?.update(order?.id, {
        collectionPlannedDate: collectionDate,
        orderStatus: 'collecte_planifiee',
      });
      setOrder(updated);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la planification');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToShipment = async () => {
    if (!window.confirm('Convertir cette commande promo en envoi ? Un nouvel envoi sera créé automatiquement.')) return;
    try {
      setConverting(true);
      setError('');
      const result = await promoSummerOrderService?.convertToShipment(order?.id);
      setOrder(result?.order);
      showSuccess(`Envoi créé avec succès : ${result?.shipment?.reference}`);
      onUpdated?.();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la conversion en envoi');
    } finally {
      setConverting(false);
    }
  };

  // ─── Invoice actions ──────────────────────────────────────────────────────

  const handleCreateDepositInvoice = async () => {
    if (depositInvoice) {
      setError('Une facture acompte existe déjà pour cette commande.');
      return;
    }
    try {
      setInvoiceAction('creating_deposit');
      setError('');
      const { invoice, isNew } = await invoiceService?.createPromoInvoice(order, 'deposit');

      // Auto-create partner operation if prestataire encaisse
      if (isNew && (order?.encaissement_type === 'prestataire' || !order?.encaissement_type) && order?.provider_id) {
        try {
          await partnerAccountingService?.createOperation({
            providerId: order?.provider_id,
            providerName: order?.provider_name || '',
            clientName: order?.client_name,
            operationType: 'livraison_barriques',
            amountCollectedByPartner: totalDeposit,
            encaissementType: 'prestataire',
            partnerShare: totalProvider,
            amountDueToAdt: totalADT,
            reversementStatus: 'a_reverser',
            promoOrderId: order?.id,
            notes: `Acompte Promo Ete 2026 — ${qty} barrique(s) — ${order?.client_name}`,
            operationDate: new Date()?.toISOString()?.split('T')?.[0],
          });
        } catch (partnerErr) {
          console.warn('Avertissement: ecriture comptabilite partenaire echouee:', partnerErr?.message);
        }
      }

      // Generate and upload PDF
      const pdfUrl = await invoiceService?.generateAndUploadPromoInvoicePDF(order, invoice);

      await loadInvoices();
      // Refresh order to get updated invoice refs
      const updatedOrder = await promoSummerOrderService?.getById(order?.id);
      setOrder(updatedOrder);

      showSuccess(`Facture acompte créée : ${invoice?.invoice_reference}`);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la création de la facture');
    } finally {
      setInvoiceAction('');
    }
  };

  const handleDownloadInvoicePDF = async (invoice) => {
    try {
      setInvoiceAction(`downloading_${invoice?.id}`);
      setError('');

      // Always fetch the latest invoice data to reflect current payment status
      const { data: freshInvoice, error: fetchErr } = await supabase
        ?.from('adt_invoices')
        ?.select('*')
        ?.eq('id', invoice?.id)
        ?.single();

      const invoiceToUse = (!fetchErr && freshInvoice) ? freshInvoice : invoice;

      // Always regenerate PDF to reflect latest payment state
      const freshOrder = await promoSummerOrderService?.getById(order?.id);
      const url = await invoiceService?.generateAndUploadPromoInvoicePDF(freshOrder || order, invoiceToUse);
      await loadInvoices();

      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      setError(err?.message || 'Erreur lors du téléchargement');
    } finally {
      setInvoiceAction('');
    }
  };

  const handleMarkDepositPaid = async () => {
    if (!depositInvoice) {
      setError('Créez d\'abord la facture acompte.');
      return;
    }
    try {
      setInvoiceAction('marking_paid');
      setError('');
      await invoiceService?.addPaymentToInvoice(depositInvoice?.id, totalDeposit, 'Acompte marqué payé');
      const updated = await promoSummerOrderService?.updateStatus(order?.id, 'acompte_paye');
      setOrder(updated);
      await loadInvoices();
      showSuccess('Acompte marqué comme payé');
    } catch (err) {
      setError(err?.message || 'Erreur lors du marquage');
    } finally {
      setInvoiceAction('');
    }
  };

  const handleAddBalancePayment = () => {
    if (!depositInvoice) {
      setError('Créez d\'abord la facture acompte.');
      return;
    }
    setSelectedInvoiceId(depositInvoice?.id);
    setPaymentAmount(String(totalBalance));
    setPaymentNotes('Paiement solde collecte');
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async () => {
    if (!paymentAmount || !selectedInvoiceId) return;
    try {
      setInvoiceAction('paying');
      setError('');
      await invoiceService?.addPaymentToInvoice(selectedInvoiceId, Number(paymentAmount), paymentNotes);
      // If full balance paid, update order status
      const invoice = invoices?.find(inv => inv?.id === selectedInvoiceId);
      const newPaid = Number(invoice?.paid_amount) + Number(paymentAmount);
      if (newPaid >= totalPromo) {
        const updated = await promoSummerOrderService?.updateStatus(order?.id, 'solde_paye');
        setOrder(updated);
      }
      await loadInvoices();
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentNotes('');
      showSuccess(`Paiement de ${paymentAmount}€ enregistré`);
    } catch (err) {
      setError(err?.message || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setInvoiceAction('');
    }
  };

  const handleSolderInvoice = async () => {
    if (!depositInvoice) {
      setError('Créez d\'abord la facture acompte.');
      return;
    }
    const remaining = Number(depositInvoice?.remaining_amount) || 0;
    if (remaining <= 0) {
      showSuccess('La facture est déjà soldée.');
      return;
    }
    if (!window.confirm(`Solder la facture ? Montant restant : ${remaining}€`)) return;
    try {
      setInvoiceAction('soldering');
      setError('');
      await invoiceService?.addPaymentToInvoice(depositInvoice?.id, remaining, 'Solde final');
      const updated = await promoSummerOrderService?.updateStatus(order?.id, 'solde_paye');
      setOrder(updated);
      await loadInvoices();
      showSuccess('Facture soldée avec succès');
    } catch (err) {
      setError(err?.message || 'Erreur lors du solde');
    } finally {
      setInvoiceAction('');
    }
  };

  const statusLabel = PROMO_STATUS_LABELS?.[order?.order_status] || order?.order_status;
  const statusColor = PROMO_STATUS_COLORS?.[order?.order_status] || 'bg-gray-100 text-gray-700';
  const isConverted = ['converti_en_envoi', 'affecte_conteneur']?.includes(order?.order_status);

  const getInvoiceStatusBadge = (status) => {
    const map = {
      issued: 'bg-blue-100 text-blue-700',
      partially_paid: 'bg-orange-100 text-orange-700',
      paid: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      draft: 'bg-gray-100 text-gray-700',
    };
    const labels = {
      issued: 'Émise',
      partially_paid: 'Partiellement payée',
      paid: 'Payée',
      cancelled: 'Annulée',
      draft: 'Brouillon',
    };
    return { color: map?.[status] || 'bg-gray-100 text-gray-700', label: labels?.[status] || status };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
              <Wine className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{order?.client_name}</h2>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                {statusLabel}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Alerts */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {successMsg}
            </div>
          )}

          {/* Client + Order Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Client</h3>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900 dark:text-white font-medium">{order?.client_name}</span>
              </div>
              {order?.client_phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">{order?.client_phone}</span>
                </div>
              )}
              {order?.client_email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">{order?.client_email}</span>
                </div>
              )}
              {order?.delivery_address && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {order?.delivery_address}<br />
                    {order?.delivery_postal_code} {order?.delivery_city}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Commande</h3>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Barriques 270L</span>
                  <span className="font-bold text-gray-900 dark:text-white">{qty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total promo</span>
                  <span className="font-bold text-amber-600">{totalPromo}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Acompte</span>
                  <span className={`font-bold ${order?.deposit_paid ? 'text-green-600' : 'text-orange-500'}`}>
                    {totalDeposit}€ {order?.deposit_paid ? '✓ payé' : '— non payé'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Solde</span>
                  <span className={`font-bold ${order?.balance_paid ? 'text-green-600' : 'text-blue-500'}`}>
                    {totalBalance}€ {order?.balance_paid ? '✓ payé' : '— non payé'}
                  </span>
                </div>
                <div className="border-t border-amber-200 dark:border-amber-800 pt-1.5 flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Prestataire</span>
                  <span className="font-medium text-orange-600">{totalProvider}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">ADT reçoit</span>
                  <span className="font-medium text-teal-600">{totalADT}€</span>
                </div>
              </div>
              {order?.provider_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">{order?.provider_name} {order?.provider_phone ? `· ${order?.provider_phone}` : ''}</span>
                </div>
              )}
              {order?.delivery_date && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Livraison : {new Date(order.delivery_date)?.toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ─── INVOICE SECTION ─────────────────────────────────────────── */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1">
              <Receipt className="h-3.5 w-3.5" /> Facturation ADT Express
            </h3>

            {loadingInvoices ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader className="h-4 w-4 animate-spin" />
                Chargement des factures...
              </div>
            ) : (
              <div className="space-y-3">
                {/* Deposit Invoice */}
                {depositInvoice ? (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">Facture acompte</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusBadge(depositInvoice?.invoice_status)?.color}`}>
                            {getInvoiceStatusBadge(depositInvoice?.invoice_status)?.label}
                          </span>
                        </div>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5 font-mono">{depositInvoice?.invoice_reference}</p>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Total</span>
                            <p className="font-bold text-gray-900 dark:text-white">{Number(depositInvoice?.total_amount)?.toFixed(0)}€</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Payé</span>
                            <p className="font-bold text-green-600">{Number(depositInvoice?.paid_amount)?.toFixed(0)}€</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Reste</span>
                            <p className={`font-bold ${Number(depositInvoice?.remaining_amount) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {Number(depositInvoice?.remaining_amount)?.toFixed(0)}€
                            </p>
                          </div>
                        </div>
                        {depositInvoice?.prestataire_name && (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            Encaissé par : {depositInvoice?.prestataire_name} ({Number(depositInvoice?.prestataire_amount)?.toFixed(0)}€) · ADT : {Number(depositInvoice?.adt_amount)?.toFixed(0)}€
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleDownloadInvoicePDF(depositInvoice)}
                          disabled={invoiceAction === `downloading_${depositInvoice?.id}`}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
                        >
                          {invoiceAction === `downloading_${depositInvoice?.id}` ? (
                            <Loader className="h-3 w-3 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3" />
                          )}
                          PDF
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-sm text-amber-700 dark:text-amber-400">
                    Aucune facture acompte créée pour cette commande.
                  </div>
                )}

                {/* Final Invoice */}
                {finalInvoice && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-green-900 dark:text-green-200">Facture finale</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusBadge(finalInvoice?.invoice_status)?.color}`}>
                            {getInvoiceStatusBadge(finalInvoice?.invoice_status)?.label}
                          </span>
                        </div>
                        <p className="text-xs text-green-700 dark:text-green-400 mt-0.5 font-mono">{finalInvoice?.invoice_reference}</p>
                      </div>
                      <button
                        onClick={() => handleDownloadInvoicePDF(finalInvoice)}
                        disabled={invoiceAction === `downloading_${finalInvoice?.id}`}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
                      >
                        {invoiceAction === `downloading_${finalInvoice?.id}` ? (
                          <Loader className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                        PDF
                      </button>
                    </div>
                  </div>
                )}

                {/* Invoice Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {!depositInvoice && (
                    <button
                      onClick={handleCreateDepositInvoice}
                      disabled={!!invoiceAction}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {invoiceAction === 'creating_deposit' ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                      Créer facture acompte
                    </button>
                  )}

                  {depositInvoice && !order?.deposit_paid && (
                    <button
                      onClick={handleMarkDepositPaid}
                      disabled={!!invoiceAction}
                      className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {invoiceAction === 'marking_paid' ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Marquer acompte payé
                    </button>
                  )}

                  {depositInvoice && order?.deposit_paid && !order?.balance_paid && (
                    <button
                      onClick={handleAddBalancePayment}
                      disabled={!!invoiceAction}
                      className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <CreditCard className="h-4 w-4" />
                      Ajouter paiement solde
                    </button>
                  )}

                  {depositInvoice && Number(depositInvoice?.remaining_amount) > 0 && (
                    <button
                      onClick={handleSolderInvoice}
                      disabled={!!invoiceAction}
                      className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {invoiceAction === 'soldering' ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <DollarSign className="h-4 w-4" />
                      )}
                      Solder la facture
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Status change */}
          {!isConverted && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Changer le statut
              </h3>
              <div className="flex flex-wrap gap-2">
                {PROMO_STATUS_ORDER?.filter(s => !['converti_en_envoi', 'affecte_conteneur']?.includes(s))?.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    disabled={saving || order?.order_status === s}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                      order?.order_status === s
                        ? 'bg-amber-600 text-white' : 'border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {PROMO_STATUS_LABELS?.[s]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Collection planning */}
          {!isConverted && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Planifier la collecte
              </h3>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={collectionDate}
                  onChange={(e) => setCollectionDate(e?.target?.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
                <button
                  onClick={handlePlanCollection}
                  disabled={!collectionDate || saving}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Planifier
                </button>
              </div>
              {order?.collection_planned_date && (
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                  Collecte planifiée le {new Date(order.collection_planned_date)?.toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          )}

          {/* Other Actions */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Autres actions</h3>
            <div className="flex flex-wrap gap-2">
              {!isConverted && (
                <button
                  onClick={handleRelance}
                  disabled={saving}
                  className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Relancer client
                </button>
              )}

              {!isConverted && (
                <button
                  onClick={handleConvertToShipment}
                  disabled={converting || saving}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {converting ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                  Convertir en envoi
                </button>
              )}
            </div>
          </div>

          {/* Linked shipment */}
          {isConverted && order?.linked_shipment_id && (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">Converti en envoi</span>
              </div>
              <p className="text-sm text-emerald-600 dark:text-emerald-500 mt-1">
                Envoi lié : {order?.linked_shipment_id?.slice(0, 8)}...
                {order?.converted_at && ` · ${new Date(order.converted_at)?.toLocaleDateString('fr-FR')}`}
              </p>
            </div>
          )}

          {/* Notes */}
          {order?.notes && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <FileText className="h-3 w-3" /> Notes
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-700 rounded-lg p-3">
                {order?.notes}
              </p>
            </div>
          )}

          {/* Relance info */}
          {order?.relance_count > 0 && (
            <div className="text-xs text-gray-400 dark:text-gray-500">
              Relancé {order?.relance_count} fois · Dernière relance :{' '}
              {order?.last_relance_at ? new Date(order.last_relance_at)?.toLocaleDateString('fr-FR') : '—'}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-slate-700">
            <button
              onClick={() => { onUpdated?.(); onClose(); }}
              className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ajouter un paiement</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Montant (€)</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="330"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <input
                  type="text"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="Paiement solde collecte"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitPayment}
                  disabled={!paymentAmount || invoiceAction === 'paying'}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {invoiceAction === 'paying' ? <Loader className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
